'use client';

import React, { Component, ErrorInfo, ReactNode } from 'react';
import { createPortal } from 'react-dom';

import PlusIcon from '@app/components/icons/PlusIcon';
import { cn } from '@libs/utils';
import { Z_INDEX } from '@libs/zIndex';

const isDev = process.env.NODE_ENV === 'development';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  showModal: boolean;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      showModal: false,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error, showModal: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    this.setState({ errorInfo });
    // Log error to console for debugging
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  componentDidMount(): void {
    // Global error handler for uncaught errors
    window.onerror = (message, source, lineno, colno, error) => {
      console.error('Global error caught:', { message, source, lineno, colno, error });
      this.setState({
        hasError: true,
        error: error || new Error(String(message)),
        showModal: true,
      });
      return true; // Prevent default browser error handling
    };

    // Handle unhandled promise rejections
    window.onunhandledrejection = event => {
      console.error('Unhandled promise rejection:', event.reason);
      this.setState({
        hasError: true,
        error: event.reason instanceof Error ? event.reason : new Error(String(event.reason)),
        showModal: true,
      });
    };
  }

  componentWillUnmount(): void {
    window.onerror = null;
    window.onunhandledrejection = null;
  }

  handleCloseModal = (): void => {
    this.setState({ showModal: false });
  };

  handleReload = (): void => {
    window.location.reload();
  };

  handleGoHome = (): void => {
    window.location.href = '/';
  };

  handleRetry = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      showModal: false,
    });
  };

  renderErrorModal(): ReactNode {
    const { showModal, error } = this.state;

    if (!showModal || typeof window === 'undefined') return null;

    const zIndex = Z_INDEX.modal + 100; // Higher than normal modals

    const modalContent = (
      <div className={cn('fixed inset-0 flex p-4 items-center justify-center')} style={{ zIndex }}>
        {/* Backdrop */}
        <div
          className={cn(
            'absolute inset-0 backdrop-blur-sm bg-gray-900/80',
            'animate-in fade-in duration-200'
          )}
          style={{ zIndex: zIndex - 1 }}
        />

        {/* Modal Content */}
        <div
          className={cn(
            'relative rounded-xl shadow-xl bg-white w-full max-w-md',
            'animate-in zoom-in-95 fade-in duration-200'
          )}
          style={{ zIndex }}
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="error-modal-title"
          aria-describedby="error-modal-description"
        >
          {/* Header */}
          <div className="p-4 border-b border-gray-200 flex items-center justify-between rounded-t-xl bg-red-50">
            <div className="flex items-center gap-3">
              {/* Error Icon */}
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                <svg
                  className="w-6 h-6 text-red-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>
              <div>
                <h2 id="error-modal-title" className="text-lg font-semibold text-gray-900">
                  Đã xảy ra lỗi
                </h2>
                <p className="text-sm text-gray-600">Rất tiếc, có sự cố xảy ra</p>
              </div>
            </div>
            <button
              onClick={this.handleCloseModal}
              className={cn(
                'w-8 h-8 text-gray-500 hover:text-gray-700',
                'flex items-center justify-center rounded-full',
                'hover:bg-gray-100 transition-colors cursor-pointer'
              )}
              aria-label="Đóng"
            >
              <PlusIcon className="w-5 h-5 rotate-45" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            <p id="error-modal-description" className="text-gray-700 mb-4">
              Ứng dụng gặp sự cố không mong muốn. Bạn có thể thử tải lại trang hoặc quay về trang
              chủ.
            </p>

            {/* Error details (collapsible) */}
            {error && isDev && (
              <details className="mb-4">
                <summary className="text-sm text-gray-500 cursor-pointer hover:text-gray-700 select-none">
                  Chi tiết lỗi
                </summary>
                <div className="mt-2 p-3 bg-gray-100 rounded-lg overflow-auto max-h-32">
                  <code className="text-xs text-red-600 whitespace-pre-wrap break-all">
                    {error.message || 'Unknown error'}
                  </code>
                </div>
              </details>
            )}

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={this.handleRetry}
                className={cn(
                  'flex-1 px-4 py-2.5 rounded-lg font-medium',
                  'bg-blue-800 text-white',
                  'hover:bg-blue-900 transition-colors',
                  'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2'
                )}
              >
                Thử lại
              </button>
              <button
                onClick={this.handleReload}
                className={cn(
                  'flex-1 px-4 py-2.5 rounded-lg font-medium',
                  'bg-gray-100 text-gray-900 border border-gray-300',
                  'hover:bg-gray-200 transition-colors',
                  'focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2'
                )}
              >
                Tải lại trang
              </button>
            </div>

            <button
              onClick={this.handleGoHome}
              className={cn(
                'w-full mt-3 px-4 py-2 rounded-lg font-medium text-sm',
                'text-gray-600 hover:text-gray-800',
                'hover:bg-gray-50 transition-colors'
              )}
            >
              Về trang chủ
            </button>
          </div>

          {/* Footer */}
          <div className="px-6 py-3 border-t border-gray-100 bg-gray-50 rounded-b-xl">
            <p className="text-xs text-gray-500 text-center">
              Nếu vấn đề tiếp tục, vui lòng liên hệ bộ phận hỗ trợ.
            </p>
          </div>
        </div>
      </div>
    );

    return createPortal(modalContent, document.body);
  }

  render(): ReactNode {
    const { hasError, showModal } = this.state;
    const { children, fallback } = this.props;

    // If there's an error but modal is closed, try to render children again (retry)
    if (hasError && !showModal) {
      return children;
    }

    // Always render children, and show modal on top if there's an error
    return (
      <>
        {hasError && fallback ? fallback : children}
        {this.renderErrorModal()}
      </>
    );
  }
}

export default ErrorBoundary;
